import {Component, EventEmitter, Input, Output} from '@angular/core';

import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faAngleLeft, faAngleRight, faAnglesLeft, faAnglesRight} from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'ui-pagination',
  imports: [FontAwesomeModule],
  templateUrl: './ui-pagination.component.html',
  styleUrl: './ui-pagination.component.scss'
})
export class UiPaginationComponent {
  faAnglesLeft = faAnglesLeft;
  faAngleLeft = faAngleLeft;
  faAngleRight = faAngleRight;
  faAnglesRight = faAnglesRight;

  @Input() currentPage = 1;
  @Input() totalPages = 1;
  @Input() totalItems = 0;
  @Input() pageSize = 25;
  @Input() pageSizeOptions: number[] = [25, 50, 100];

  /**
   * Maximum number of page buttons to show
   */
  @Input() maxVisiblePages = 5;

  /**
   * Maximum number of page buttons to show on small screens.
   * Keeps the control on a single row on mobile.
   */
  @Input() maxVisiblePagesMobile = 3;

  /**
   * Show boundary buttons (first/last)
   */
  @Input() showBoundaryButtons = true;

  /**
   * Show the page size selector
   */
  @Input() showPageSizeSelector = true;

  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();

  get startItem(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItem(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  private computeVisiblePages(maxVisible: number): number[] {
    const safeMax = Math.max(1, Math.floor(maxVisible));

    const pages: number[] = [];
    const halfVisible = Math.floor(safeMax / 2);

    let startPage = Math.max(1, this.currentPage - halfVisible);
    let endPage = Math.min(this.totalPages, startPage + safeMax - 1);

    // Adjust if we're near the end
    if (endPage - startPage < safeMax - 1) {
      startPage = Math.max(1, endPage - safeMax + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  get visiblePages(): number[] {
    return this.computeVisiblePages(this.maxVisiblePages);
  }

  get visiblePagesMobile(): number[] {
    return this.computeVisiblePages(this.maxVisiblePagesMobile);
  }

  get showLeftEllipsis(): boolean {
    return this.visiblePages.length > 0 && this.visiblePages[0] > 1;
  }

  get showRightEllipsis(): boolean {
    return this.visiblePages.length > 0 &&
           this.visiblePages[this.visiblePages.length - 1] < this.totalPages;
  }

  get showLeftEllipsisMobile(): boolean {
    return this.visiblePagesMobile.length > 0 && this.visiblePagesMobile[0] > 1;
  }

  get showRightEllipsisMobile(): boolean {
    return this.visiblePagesMobile.length > 0 &&
           this.visiblePagesMobile[this.visiblePagesMobile.length - 1] < this.totalPages;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) {
      return;
    }
    this.pageChange.emit(page);
  }

  nextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  previousPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  firstPage(): void {
    this.goToPage(1);
  }

  lastPage(): void {
    this.goToPage(this.totalPages);
  }

  onPageSizeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const newSize = parseInt(target.value, 10);
    if (!isNaN(newSize)) {
      this.pageSizeChange.emit(newSize);
    }
  }
}
